import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Book, Filter, X } from 'lucide-react';
import { novelAPI } from '../../services/api';
import { useGenreStore } from '../../hooks/useStore';
import NovelCard from '../../components/NovelCard/NovelCard';
import './Search.css';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [novels, setNovels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const { genres } = useGenreStore();
  
  // Filters
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
    if (query) {
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedGenre, selectedStatus, sortBy]);

  const performSearch = async () => {
    setIsLoading(true);
    try {
      const params = {
        q: query,
        sort: sortBy,
        ...(selectedGenre && { genre: selectedGenre }),
        ...(selectedStatus && { status: selectedStatus })
      };
      const response = await novelAPI.search(params);
      setNovels(response.data || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  const clearFilters = () => {
    setSelectedGenre('');
    setSelectedStatus('');
    setSortBy('popular');
  };

  return (
    <main className="search-page">
      <div className="container">
        {/* Search Header */}
        <div className="search-header">
          <h1>Search Novels</h1>
          
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-wrapper">
              <SearchIcon size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Enter novel title, author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button type="button" className="clear-btn" onClick={() => setSearchQuery('')}>
                  <X size={18} />
                </button>
              )}
            </div>
            <button type="submit" className="search-btn">
              Search
            </button>
          </form>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={18} />
            Filters
          </button>

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Genre</label>
                <select value={selectedGenre} onChange={(e) => setSelectedGenre(e.target.value)}>
                  <option value="">All</option>
                  {genres.map((genre) => (
                    <option key={genre._id} value={genre._id}>
                      {genre.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Status</label>
                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Sort By</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="popular">Popular</option>
                  <option value="newest">Newest</option>
                  <option value="rating">Top Rated</option>
                  <option value="chapters">Most Chapters</option>
                </select>
              </div>

              <button className="clear-filters" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {query && (
          <div className="search-results">
            <div className="results-header">
              <p>
                {isLoading ? 'Searching...' : (
                  novels.length > 0 
                    ? `Found ${pagination?.total || novels.length} results for "${query}"`
                    : `No results found for "${query}"`
                )}
              </p>
            </div>

            {isLoading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
              </div>
            ) : novels.length > 0 ? (
              <div className="novels-grid">
                {novels.map((novel) => (
                  <NovelCard key={novel._id} novel={novel} />
                ))}
              </div>
            ) : (
              <div className="no-results">
                <Book size={48} />
                <h3>No novels found</h3>
                <p>Try searching with different keywords or adjust filters</p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!query && (
          <div className="empty-state">
            <SearchIcon size={48} />
            <h3>Search for your favorite novels</h3>
            <p>Enter novel title or author name to start searching</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default Search;
